import { createApi } from '@reduxjs/toolkit/query/react';
import {
  columnsStopLoading,
  rowsCountStopLoading,
  rowsStopLoading,
  setQueryExecutionTime,
  startQueryTimer,
  stepsStartLoading,
  stepsStopLoading,
} from '@/features/query-builder/queryBuilderSlice';
import { configureBaseQuery } from '@/api/baseQuery';
import { useDatasourceId, useQueryId } from '@/hooks';
import { QueryStep, UUID } from '@/types';
import { latestRequestIds } from './latestRequestIds';
import { latestAdHocPromises } from './latestAdHocPromises';

export const SAVE_QUERY_STEPS = 'saveQuerySteps';
export const GET_QUERY_STEPS = 'getQuerySteps';
export const GET_QUERY_COLUMNS = 'getQueryColumns';
export const GET_QUERY_ROWS = 'getQueryRows';
export const GET_QUERY_ROWS_COUNT = 'getQueryRowsCount';

export const queryBuilderQueriesApi = createApi({
  reducerPath: 'queryBuilderQueriesApi',
  baseQuery: configureBaseQuery('/api/v1/datasources/'),
  endpoints: (builder) => ({
    [SAVE_QUERY_STEPS]: builder.mutation<
      { queryId: UUID },
      { datasourceId: UUID; steps: QueryStep[]; page?: number }
    >({
      query: ({ datasourceId, steps, page = 1 }) => ({
        url: `${datasourceId}/queries/steps?page=${page}`,
        method: 'POST',
        body: steps,
      }),
      async onQueryStarted(
        { datasourceId, steps },
        { dispatch, queryFulfilled, requestId },
      ) {
        latestRequestIds.steps = requestId;
        latestAdHocPromises.steps = queryFulfilled;

        dispatch(startQueryTimer());
        dispatch(stepsStartLoading());

        const { data } = await queryFulfilled;
        if (data) {
          await dispatch(
            queryBuilderQueriesApi.util.upsertQueryData(
              GET_QUERY_STEPS,
              {
                datasourceId,
                queryId: data.queryId,
              },
              steps,
            ),
          );
        }

        if (latestRequestIds.steps === requestId) {
          dispatch(stepsStopLoading());
        }
      },
    }),
    [GET_QUERY_STEPS]: builder.query<
      any,
      { datasourceId: UUID; queryId?: UUID; asSQL?: boolean }
    >({
      query: ({ datasourceId, queryId, asSQL = false }) =>
        `${datasourceId}/queries/${queryId}/steps?asSQL=${asSQL}`,
      transformResponse: (data: any, _meta, { asSQL }) =>
        asSQL ? data : data.steps,
      async onQueryStarted({ asSQL }, { dispatch, queryFulfilled, requestId }) {
        if (asSQL) return;

        latestRequestIds.steps = requestId;
        latestAdHocPromises.steps = queryFulfilled;

        dispatch(startQueryTimer());
        dispatch(stepsStartLoading());

        await queryFulfilled;

        if (latestRequestIds.steps === requestId) {
          dispatch(stepsStopLoading());
        }
      },
    }),
    [GET_QUERY_COLUMNS]: builder.query<
      any,
      {
        datasourceId: UUID;
        queryId?: UUID;
        layerInEditModeId?: number;
        stepInEditModeIndex?: Maybe<number>;
        includeHidden?: boolean;
      }
    >({
      query: ({
        datasourceId,
        queryId,
        layerInEditModeId,
        stepInEditModeIndex,
        includeHidden,
      }) => {
        const params =
          [
            [layerInEditModeId, 'layerInEditModeId'],
            [stepInEditModeIndex, 'stepInEditModeIndex'],
            [includeHidden, 'includeHidden'],
          ]
            .filter(([val]) => val)
            .map(([val, key]) => `${key}=${val}`)
            .join('&') || '';

        return `${datasourceId}/queries/${queryId}/columns${
          params && `?${params}`
        }`;
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.columns = requestId;
        latestAdHocPromises.columns = queryFulfilled;

        try {
          await queryFulfilled;
        } catch (_e) {
          dispatch(rowsStopLoading());
          dispatch(rowsCountStopLoading());
        }
        if (latestRequestIds.columns === requestId) {
          dispatch(columnsStopLoading());
        }
      },
    }),
    [GET_QUERY_ROWS]: builder.query<
      any,
      { datasourceId: UUID; queryId?: UUID }
    >({
      query: ({ datasourceId, queryId }) =>
        `${datasourceId}/queries/${queryId}/rows`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.rows = requestId;
        latestAdHocPromises.rows = queryFulfilled;

        try {
          const { data } = await queryFulfilled;

          if (latestRequestIds.rows === requestId) {
            dispatch(setQueryExecutionTime(Date.now()));
            dispatch(rowsStopLoading());

            if (data.dataTable._rows.length <= 100) {
              dispatch(rowsCountStopLoading());
            }
          }
        } catch (_e) {
          if (latestRequestIds.rows === requestId) {
            dispatch(rowsStopLoading());
            dispatch(rowsCountStopLoading());
          }
        }
      },
    }),
    [GET_QUERY_ROWS_COUNT]: builder.query<
      any,
      { datasourceId: UUID; queryId?: UUID }
    >({
      query: ({ datasourceId, queryId }) =>
        `${datasourceId}/queries/${queryId}/rows/count`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.rowsCount = requestId;
        latestAdHocPromises.rowsCount = queryFulfilled;

        try {
          await queryFulfilled;

          if (latestRequestIds.rowsCount === requestId) {
            dispatch(rowsCountStopLoading());
          }
        } catch (_e) {
          //
        }
      },
    }),
  }),
});

export const {
  useSaveQueryStepsMutation,
  useGetQueryStepsQuery,
  useGetQueryColumnsQuery,
  useGetQueryRowsQuery,
  useGetQueryRowsCountQuery,
  usePrefetch,
} = queryBuilderQueriesApi;

export const usePrefetchStepColumns = (
  layerInEditModeId: number,
  stepInEditModeIndex: number,
  skip = false,
) => {
  const datasourceId = useDatasourceId();
  const queryId = useQueryId();
  const prefetch = usePrefetch(GET_QUERY_COLUMNS);

  if (!queryId || skip) return () => {};

  return () =>
    prefetch({
      datasourceId,
      queryId,
      layerInEditModeId,
      stepInEditModeIndex,
      includeHidden: true,
    });
};

export const useGetQueryStepsAsSQL = () => {
  const datasourceId = useDatasourceId();
  const queryId = useQueryId();

  return useGetQueryStepsQuery(
    { datasourceId, queryId, asSQL: true },
    { skip: !queryId },
  );
};

export const useSaveQuerySteps = () => {
  const datasourceId = useDatasourceId();
  const [mutate, result] = useSaveQueryStepsMutation();

  const handleMutate = (steps: QueryStep[], page = 1) =>
    mutate({ datasourceId, steps, page });

  return [handleMutate, result] as const;
};
